import { DialogActions, DialogContent, DialogTitle, Stack, Typography } from '@mui/material';
import { useAppTFunction } from '@retail/app/i18n';
import { Checkbox } from '@retail/components';
import { useCreateAssortmentTags } from '@retail/my-assortment/data-access';
import { DateRangeSchema, DatesForm } from '@retail/products/components';
import { getISO_8601Date } from '@retail/utils';
import { ContainedButton, Dialog, TextButton, TextInputField } from '@shared/components';
import { useDisclosure } from '@shared/hooks';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { useFormContext } from 'react-hook-form';

interface Props {
  open: boolean;
  assortmentId: number;
  includeViewForMemberToggle: boolean;
  includeSendEvents: boolean;
  onToggle: () => void;
}

export function CreateAssortmentDialog({
  open,
  assortmentId,
  includeViewForMemberToggle,
  includeSendEvents,
  onToggle,
}: Props) {
  const t = useAppTFunction();
  const queryClient = useQueryClient();

  const { mutateAsync: createAssortmentTag, isPending: loadingCreateAssortmentTag } =
    useCreateAssortmentTags({ assortmentId });

  const { control, handleSubmit } = useFormContext<DateRangeSchema>();

  const [assortmentTagName, setAssortmentTagName] = useState<string>('');
  const { isOpen: sendEvents, onToggle: toggleSendEvents } = useDisclosure();
  const { isOpen: showToMembers, onToggle: toggleShowToMembers } = useDisclosure();
  const { isOpen: selectableByMembers, onToggle: toggleSelectableByMembers } = useDisclosure();

  const resetCreateValues = () => setAssortmentTagName('');

  const handlePostAssortmentTag = useCallback(
    ({ fromDate, toDate }: DateRangeSchema) => {
      createAssortmentTag({
        body: {
          name: assortmentTagName,
          sendEvents,
          visibleToMembers: showToMembers,
          selectableByMembers,
          validFrom: getISO_8601Date(fromDate),
          validTo: toDate ? getISO_8601Date(toDate) : undefined,
        },
      }).then(() => {
        queryClient.invalidateQueries({ queryKey: ['assortment-tags'] });
        resetCreateValues();
        onToggle();
      });
    },
    [
      assortmentTagName,
      createAssortmentTag,
      onToggle,
      queryClient,
      selectableByMembers,
      sendEvents,
      showToMembers,
    ]
  );

  return (
    <Dialog open={open} onClose={onToggle}>
      <DialogTitle>{t('myAssortment.centralAssortments.make')}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <TextInputField
          label={t('myAssortment.columns.name')}
          fullWidth
          margin="none"
          value={assortmentTagName}
          onChange={(e) => setAssortmentTagName(e.target.value)}
          startAdornment={<Typography color="grey.200">|</Typography>}
          size="small"
          placeholder={t('myAssortment.centralAssortments.assortmentName')}
        />
        <Stack pl={1}>
          {includeSendEvents && (
            <Checkbox label={t('myAssortment.columns.sendEvents')} onClick={toggleSendEvents} />
          )}
          {includeViewForMemberToggle && (
            <>
              <Checkbox
                label={t('myAssortment.columns.showToMember')}
                onClick={toggleShowToMembers}
              />
              <Checkbox
                label={t('myAssortment.columns.selectableByMembers')}
                onClick={toggleSelectableByMembers}
              />
            </>
          )}
        </Stack>
        <DatesForm />
      </DialogContent>
      <DialogActions>
        <TextButton onClick={onToggle} size="small">
          {t('myAssortment.cancel')}
        </TextButton>
        <ContainedButton
          sx={{ alignSelf: 'flex-end' }}
          size="small"
          color="secondary"
          disabled={
            !assortmentTagName ||
            control.getFieldState('fromDate').invalid ||
            loadingCreateAssortmentTag
          }
          onClick={handleSubmit(handlePostAssortmentTag)}
        >
          {t('myAssortment.centralAssortments.create')}
        </ContainedButton>
      </DialogActions>
    </Dialog>
  );
}
